<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useIpTableSettings } from '@/composables/useIpTableSettings'
import IpResultsTable from './IpResultsTable.vue'
import IpTableSidebar from './IpTableSidebar.vue'

const props = defineProps({
  data: {
    type: Array,
    required: true
  }
})

const router = useRouter()
const fieldSettings = useIpTableSettings()

// Cache enabled fields to prevent unnecessary re-renders
const enabledFields = computed(() => fieldSettings.getOrderedEnabledFields())

// Navigation handlers
function handleIpNavigation(ip) {
  router.push({
    name: 'SearchResults',
    query: {
      input: ip,
      type: 'ip'
    }
  })
}

function handleEventSearchNavigation(ip) {
  router.push({
    name: 'EventSearch',
    query: {
      query: ip
    }
  })
}

// Export handlers
function download(content, filename, type) {
  const blob = new Blob([content], { type: type + ';charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}

function handleExport(format) {
  const data = props.data

  if (format === 'jsonl') {
    let content = ''
    for (let i = 0; i < data.length; i++) {
      content += JSON.stringify(data[i]) + '\n'
    }
    download(content, 'ip-results.jsonl', 'application/json')
  } else if (format === 'json') {
    const content = JSON.stringify(data, null, 2)
    download(content, 'ip-results.json', 'application/json')
  } else if (format === 'csv') {
    // Helper to get nested value
    const getValue = (obj, path) => {
      if (!path) return obj
      return path.split('.').reduce((acc, part) => acc && acc[part], obj)
    }

    // Flatten an object to dot notation keys
    const flattenObject = (obj, prefix = '') => {
      const result = {}
      for (const [key, value] of Object.entries(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          Object.assign(result, flattenObject(value, fullKey))
        } else {
          result[fullKey] = value
        }
      }
      return result
    }

    // Flatten all rows and collect all unique keys
    const flattenedRows = data.map(row => flattenObject(row))
    const allKeys = new Set()
    for (const row of flattenedRows) {
      for (const key of Object.keys(row)) {
        allKeys.add(key)
      }
    }

    // Sort keys for consistent column order
    const sortedKeys = Array.from(allKeys).sort()

    // Format value for CSV
    const formatValue = (value) => {
      if (value === null || value === undefined) return ''
      if (Array.isArray(value)) {
        return value.map(v => typeof v === 'object' ? JSON.stringify(v) : v).join('; ')
      }
      return String(value)
    }

    // Escape CSV value
    const escapeCSV = (value) => {
      let str = formatValue(value)
      // Neutralise CSV formula injection: prefix values starting with characters
      // that spreadsheet applications interpret as formulas (=, +, -, @, \t, |)
      // with [PROT]. Excel/Sheets treat the prefixed value as a plain text string.
      if (/^[=+\-@\t|]/.test(str)) {
        str = '[PROT]' + str
      }
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"'
      }
      return str
    }

    // Build CSV
    let csv = sortedKeys.join(',') + '\n'
    for (const row of flattenedRows) {
      const values = sortedKeys.map(key => escapeCSV(row[key]))
      csv += values.join(',') + '\n'
    }
    download(csv, 'ip-results.csv', 'text/csv')
  }
}
</script>

<template>
  <div class="ip-table-view">
    <IpResultsTable
      :data="data"
      :enabled-fields="enabledFields"
      @navigate-ip="handleIpNavigation"
      @navigate-event-search="handleEventSearchNavigation"
    />
    <IpTableSidebar
      :data="data"
      :is-field-enabled="fieldSettings.isFieldEnabled"
      :get-field-order="fieldSettings.getFieldOrder"
      :toggle-field="fieldSettings.toggleField"
      :set-field-order="fieldSettings.setFieldOrder"
      :reset-settings="fieldSettings.resetSettings"
      :has-custom-settings="fieldSettings.hasCustomSettings"
      :get-enabled-count="fieldSettings.getEnabledCount"
      :get-total-count="fieldSettings.getTotalCount"
      @export="handleExport"
    />
  </div>
</template>

<style scoped>
.ip-table-view {
  display: flex;
  height: 100%;
  width: 100%;
  gap: 0;
}
</style>
